import { AbstractControl, ValidationErrors } from '@angular/forms';
import {
  GENERIC_ASSISTANT_CONFIGURATION_FILENAME,
  isReservedSkillMdFilename,
} from '@shared';
import type { AssistantBuilderTraitGenericFilesValue } from './assistant-builder-trait-generic-form.validators';

const isExactSkillMd = (file: { name: string }): boolean =>
  file.name === GENERIC_ASSISTANT_CONFIGURATION_FILENAME;

/**
 * Case/whitespace-insensitive skill.md DETECTION (QA#2), via the shared
 * `isReservedSkillMdFilename` predicate — the same one the staging validator
 * and hs-api's gates read (SB-3515 T25), so the definition of "skill.md-like"
 * cannot drift between layers. Acceptance stays exact-match: the server
 * contract (`assertSkillMdAssistantDocumentFilenames`, PR-2) requires the
 * exact `skill.md` name, and the UI must not accept a file the API will
 * reject (Stage 3 plan, open question 4 standing default).
 */
const isSkillMdLike = (file: { name: string }): boolean =>
  isReservedSkillMdFilename(file.name);

/**
 * Group-validator factory for the skill.md configuration-file control shared
 * by the generic, comparison and compliance trait forms (SB-3325 T4).
 *
 * Valid ⇔ the config control holds exactly one file named exactly `skill.md`,
 * and no skill.md-like file sits in any of the other file controls.
 *
 * Errors (distinct so getFormErrorMessage can tell them apart):
 * - `{ skillMdConfigurationFile: true }` — missing / not-exactly-one config
 *   file, or a skill.md misplaced among the other controls' files;
 * - `{ skillMdFilenameNearMiss: { actual } }` — exactly one config file whose
 *   name is not `skill.md` (including case variants like `Skill.MD`).
 */
export function skillMdConfigurationFileValidator(
  configControlName: string,
  otherFilesControlNames: string[] = [],
): (group: AbstractControl) => ValidationErrors | null {
  return (group: AbstractControl): ValidationErrors | null => {
    const configurationFiles =
      (group.get(configControlName)?.value as
        AssistantBuilderTraitGenericFilesValue | undefined) ?? [];
    const otherFiles = otherFilesControlNames.flatMap(
      (controlName) =>
        (group.get(controlName)?.value as
          AssistantBuilderTraitGenericFilesValue | undefined) ?? [],
    );

    const hasSkillMdInOtherControls = otherFiles.some(isSkillMdLike);
    if (configurationFiles.length !== 1 || hasSkillMdInOtherControls) {
      return { skillMdConfigurationFile: true };
    }

    const configurationFile = configurationFiles[0];
    if (!configurationFile || !isExactSkillMd(configurationFile)) {
      return {
        skillMdFilenameNearMiss: { actual: configurationFile?.name ?? '' },
      };
    }

    return null;
  };
}

/** The two group-level error keys above, which the configuration zone renders. */
const SKILL_MD_ERROR_KEYS = [
  'skillMdConfigurationFile',
  'skillMdFilenameNearMiss',
] as const;

/**
 * The subset of a group's errors that the configuration zone owns, or null when
 * it owns none (#1454).
 *
 * Lives beside the validator that produces those keys so the two cannot drift.
 * Callers must NOT hand `getFormErrorMessage` a whole group instead: it resolves
 * `filesRequired` first, and on the compliance form both that key and a
 * `skillMd*` key sit on the group at once when the reference zone is empty too
 * — so the wholesale form prints "At least one reference document is required."
 * under Instructions.
 *
 * Narrowing to `Record<string, unknown>` keeps the read off `ValidationErrors`'
 * `any`-typed index signature.
 */
export function pickSkillMdErrors(
  errors: ValidationErrors | null,
): ValidationErrors | null {
  if (!errors) {
    return null;
  }
  const all = errors as Record<string, unknown>;
  const owned = SKILL_MD_ERROR_KEYS.filter((key) => all[key] !== undefined).map(
    (key): [string, unknown] => [key, all[key]],
  );
  return owned.length ? Object.fromEntries(owned) : null;
}
